/**
 * Every service on the site, in one place.
 *
 * Routing, navigation, dropdowns, category pages, the homepage
 * overview and the sitemap are all generated from this file — adding
 * a service here is the only step needed to publish one.
 *
 * `category` maps to the four pillars of the practice. Physiotherapy
 * services each get their own page (they carry real search volume);
 * the other three are presented as one page per category.
 */

export const categories = {
  physiotherapy: {
    slug: "physiotherapy",
    label: "Physiotherapy",
    // physiotherapy children each get a dedicated page
    childPages: true,
    title: "Physiotherapy in Gurugram",
    lead: "Assessment-led rehabilitation for injury, pain and loss of movement — from acute sports injury to long-term neurological recovery.",
  },
  osteopathy: {
    slug: "osteopathy-chiropractic",
    label: "Osteopathy & Chiropractic Care",
    childPages: false,
    title: "Osteopathy & Chiropractic Care",
    lead: "Hands-on structural work for the spine, pelvis and peripheral joints — applied where the examination says it is warranted, not by routine.",
  },
  oriental: {
    slug: "oriental-medicine",
    label: "Oriental Medicine",
    childPages: false,
    title: "Oriental & Alternative Medicine",
    lead: "Traditions that read the body as a whole system. Offered alongside clinical rehabilitation — never in place of a diagnosis.",
  },
  fitness: {
    slug: "fitness-programs",
    label: "Fitness Programs",
    childPages: false,
    title: "Fitness & Conditioning Programs",
    lead: "Where rehabilitation ends, conditioning begins. Every programme is graded against your reassessment findings, not a class timetable.",
  },
};

export const services = [
  /* ---------------- Physiotherapy ---------------- */
  {
    slug: "orthopedic-sports-physiotherapy",
    category: "physiotherapy",
    icon: "joint",
    title: "Orthopedic & Sports Physiotherapy",
    short:
      "Joint, muscle and tendon injuries — from sprains and frozen shoulder to the overuse patterns that come with training.",
    intro:
      "Most orthopedic pain is mechanical: a tissue is being loaded in a way it cannot currently tolerate. The assessment establishes which structure is involved and why the load is landing there, then treatment reduces the irritation and rebuilds the tissue's capacity so the same thing does not happen again.",
    helps: [
      "Ligament sprains and muscle strains",
      "Frozen shoulder and rotator cuff pain",
      "Tennis elbow and golfer's elbow",
      "Knee pain, ACL and meniscus rehabilitation",
      "Ankle sprains and shin splints",
      "Tendinopathy and overuse injuries",
    ],
    expect: [
      "Movement, strength and joint testing to isolate the involved structure",
      "Manual therapy and soft tissue work to settle the acute symptoms",
      "A graded loading programme progressed on measured criteria",
      "Return-to-sport testing before you go back to full training",
    ],
  },
  {
    slug: "pre-post-surgical-rehabilitation",
    category: "physiotherapy",
    icon: "surgical",
    title: "Pre & Post-Surgical Rehabilitation",
    short:
      "Preparing a joint before an operation, and rebuilding range, strength and confidence after it.",
    intro:
      "What you can do before surgery strongly influences how quickly you recover after it. Prehabilitation builds the strength and range you will draw on during recovery; post-operative rehabilitation then follows your surgeon's protocol and timeline, progressing only as the healing tissue allows.",
    helps: [
      "Knee and hip replacement recovery",
      "ACL and meniscus reconstruction",
      "Rotator cuff and shoulder surgery",
      "Spinal surgery rehabilitation",
      "Post-fracture and post-immobilisation stiffness",
      "Scar tissue and range restoration",
    ],
    expect: [
      "Pre-operative strengthening and movement education",
      "Protocol-led progression coordinated with your surgeon's timeline",
      "Swelling, scar and range management in the early phase",
      "Graded return to full weight-bearing and daily activity",
    ],
  },
  {
    slug: "neurological-rehabilitation",
    category: "physiotherapy",
    icon: "neuro",
    title: "Neurological Rehabilitation",
    short:
      "Retraining balance, gait and motor control at the pace the nervous system will actually allow.",
    intro:
      "Neurological rehabilitation works with the nervous system's capacity to reorganise. Progress is measured in control rather than in load — repetition, specificity and patience matter more than intensity, and the programme is paced to what the system can consolidate rather than what it can attempt.",
    helps: [
      "Stroke recovery and hemiplegia",
      "Facial palsy and Bell's palsy",
      "Peripheral neuropathy and nerve injury",
      "Parkinson's-related movement difficulty",
      "Balance and coordination disorders",
      "Gait retraining after neurological injury",
    ],
    expect: [
      "Assessment of tone, sensation, balance and functional movement",
      "Task-specific retraining of the movements you need most",
      "Gait and transfer practice with progressive independence",
      "A home programme carers can support safely",
    ],
  },
  {
    slug: "geriatric-physiotherapy",
    category: "physiotherapy",
    icon: "geriatric",
    title: "Geriatric Physiotherapy",
    short:
      "Mobility, strength and fall prevention for older adults — treated as a capacity problem, not an inevitability.",
    intro:
      "Loss of mobility with age is rarely just age. It is usually strength, balance and confidence declining together, each one accelerating the others. Treatment addresses all three, because restoring walking distance and the ability to get out of a chair unaided is what preserves independence.",
    helps: [
      "Fall risk and balance decline",
      "Osteoarthritis of the knee and hip",
      "Osteoporosis-related movement caution",
      "Post-fracture recovery, particularly hip",
      "General deconditioning and reduced walking distance",
      "Joint stiffness and reduced independence",
    ],
    expect: [
      "Balance, gait and fall-risk assessment",
      "Progressive strength work suited to your starting point",
      "Home safety and mobility-aid guidance",
      "A programme designed to be sustainable, not exhausting",
    ],
  },
  {
    slug: "posture-ergonomic-correction",
    category: "physiotherapy",
    icon: "posture",
    title: "Posture & Ergonomic Correction",
    short:
      "For desk-driven neck, shoulder and back pain — treating the position you spend eight hours in, not just the symptom.",
    intro:
      "Postural pain is a dose problem: no position is harmful in itself, but any position held long enough becomes a load the tissue cannot tolerate. Treatment relieves the loaded structures and then changes the dose — through workstation setup, movement breaks and the specific strength that lets you hold a better position without effort.",
    helps: [
      "Desk and screen-related neck pain",
      "Upper back and shoulder blade ache",
      "Tension headaches from neck posture",
      "Rounded shoulders and forward head position",
      "Repetitive strain from keyboard and mouse use",
      "Lower back pain from prolonged sitting",
    ],
    expect: [
      "Postural and workstation assessment, including your actual setup",
      "Manual therapy for the loaded and restricted segments",
      "Specific strengthening so good position stops feeling like effort",
      "Practical movement-break strategy for a working day",
    ],
  },
  {
    slug: "vestibular-rehabilitation",
    category: "physiotherapy",
    icon: "vestibular",
    title: "Vestibular Rehabilitation",
    short:
      "For vertigo, dizziness and balance disturbance arising from the inner ear.",
    intro:
      "Vertigo is a symptom, not a diagnosis, and the useful question is which mechanism is producing it. Positional vertigo caused by displaced crystals in the inner ear often responds to a repositioning manoeuvre within one or two sessions. Other causes need graded habituation and gaze-stability training instead. The assessment separates them.",
    helps: [
      "BPPV (positional vertigo)",
      "Dizziness on rolling over or looking up",
      "Balance disturbance after an inner-ear infection",
      "Vestibular neuritis and labyrinthitis recovery",
      "Unsteadiness and visual motion sensitivity",
      "Post-concussion dizziness",
    ],
    expect: [
      "Positional testing to identify the mechanism and the side involved",
      "Repositioning manoeuvres where the findings indicate them",
      "Gaze stability and habituation exercises",
      "Referral onward if the pattern suggests a non-vestibular cause",
    ],
  },

  /* ---------------- Osteopathy & Chiropractic ---------------- */
  {
    slug: "spinal-alignment-joint-restoration",
    category: "osteopathy",
    icon: "spine",
    title: "Spinal Alignment & Joint Restoration",
    short:
      "Structural assessment and manual correction across the spine, pelvis and peripheral joints.",
    intro:
      "Where a joint has lost its normal range or is moving asymmetrically, restoring that movement can relieve the surrounding muscular guarding quickly. The examination establishes which segment is genuinely restricted before anything is mobilised — technique is chosen from findings, not applied by routine.",
    helps: [
      "Restricted spinal segments",
      "Pelvic and sacroiliac asymmetry",
      "Rib and thoracic restriction",
      "Peripheral joint stiffness",
    ],
  },
  {
    slug: "back-neck-sciatica-relief",
    category: "osteopathy",
    icon: "joint",
    title: "Chronic Back, Neck & Sciatica Relief",
    short:
      "Persistent spinal pain and nerve-referred symptoms into the arm or leg.",
    intro:
      "Long-standing back and neck pain usually involves more than one contributor — a sensitised structure, protective muscle guarding, and a movement pattern built around avoiding the pain. Treatment addresses the mechanical driver and then rebuilds tolerance, because relief that is not followed by loading tends not to last.",
    helps: [
      "Disc-related pain and sciatic referral",
      "Cervical spondylosis and neck stiffness",
      "Facet joint and sacroiliac pain",
      "Radiating arm or leg symptoms",
    ],
  },
  {
    slug: "craniosacral-therapy",
    category: "osteopathy",
    icon: "cranio",
    title: "Craniosacral Therapy (CST)",
    short:
      "Very light sustained contact at the skull, spine and sacrum to release deep protective tension.",
    intro:
      "Craniosacral therapy uses extremely light, sustained contact rather than force. It is typically used where the nervous system is highly protective and stronger manual work would provoke guarding — persistent headache, jaw tension and stress-driven pain patterns. It is offered as an adjunct within a broader plan, not as a stand-alone answer.",
    helps: [
      "Persistent headache and migraine patterns",
      "Jaw and temporomandibular tension",
      "Stress-driven muscular holding",
      "Post-concussion symptom sensitivity",
    ],
  },
  {
    slug: "manual-therapy-mobility",
    category: "osteopathy",
    icon: "iastm",
    title: "Manual Therapy & Mobility Restoration",
    short:
      "Hands-on soft tissue and joint work to restore range before it is loaded again.",
    intro:
      "Manual therapy opens a window: it reduces pain and improves range for a period afterwards. What matters is what is done inside that window. Every session pairs the hands-on work with loading, so the range gained is consolidated rather than lost by the next visit.",
    helps: [
      "Myofascial restriction and trigger points",
      "Post-immobilisation stiffness",
      "Reduced joint range",
      "Muscular guarding and spasm",
    ],
  },

  /* ---------------- Oriental Medicine ---------------- */
  {
     slug: "cupping-gua-sha",
    category: "oriental",
    icon: "cupping",
    /* Runs full width with an image panel — it is the modality people
       recognise and search for most, so it leads the page. */
    featured: true,
    art: "cupping",
    title: "Cupping Therapy & Gua Sha",
    short:
      "Suction and scraping techniques that lift fascia and improve local circulation.",
    intro:
      "Cupping applies negative pressure to lift the fascial layer and decompress the tissue beneath it; Gua Sha uses a smooth-edged tool drawn across the skin to address surface restriction. Both commonly leave temporary marking that is not bruising and settles within a few days. They are used to open a stubborn region before it is loaded again.",
    helps: [
      "Tight, restricted muscle groups",
      "Upper back and shoulder tension",
      "Reduced local circulation",
      "Fascial restriction",
    
    ],
  },
  {
    slug: "microcurrent-therapy",
    category: "oriental",
    icon: "microcurrent",
    title: "Microcurrent Therapy",
    short:
      "Sub-sensory electrical current matched to the body's own signalling to support tissue repair.",
    intro:
      "Microcurrent works below the threshold of sensation — most people feel nothing at all. It is used to support tissue repair and reduce inflammatory pain, generally in the earlier or more irritable phases when stronger manual work would not be tolerated.",
    helps: [
      "Acute and irritable soft tissue injury",
      "Inflammatory pain",
      "Slow-healing tissue",
      "Post-surgical swelling",
    ],
  },
  {
    slug: "moxibustion",
    category: "oriental",
    icon: "moxa",
    title: "Moxibustion",
    short:
      "Warmth from smouldering mugwort held over a point or channel.",
    intro:
      "Moxibustion applies sustained, penetrating heat over a point or channel. In its classical framework it is used for cold, stiff, chronic presentations — the kind that ease with warmth and worsen in winter — and it is often paired with needling rather than used alone.",
    helps: [
      "Chronic stiffness that eases with heat",
      "Cold-sensitive joint pain",
      "Long-standing muscular tightness",
      "Slow-resolving chronic presentations",
    ],
  },
  {
   slug: "acupuncture-electro-acupuncture",
    category: "oriental",
    icon: "needle",
    title: "Acupuncture & Electro-Acupuncture",
    short:
      "Fine needles at specific points to settle pain and release trigger points, with optional micro-current stimulation.",
    intro:
      "Needling is used here to reduce pain and release myofascial trigger points so a restricted region becomes ready for the rehabilitation that follows. Electro-acupuncture adds a low-level current between needles for a stronger, more sustained effect in stubborn or long-standing presentations.",
    helps: [
      "Myofascial trigger points and referred pain",
      "Chronic neck and low back pain",
      "Tension headaches",
      "Persistent muscular tightness",
    ],
  },
  {
    slug: "iastm",
    category: "oriental",
    icon: "iastm",
    title: "IASTM",
    short:
      "Instrument-assisted soft tissue mobilisation for adhesion and scar tissue.",
    intro:
      "A contoured steel tool reads adhesion and scar tissue that the hand can pass over, then works it down along the line of the fibres. It is particularly useful over old scar tissue and long-standing fascial restriction, and is always followed by loading so the change is retained.",
    helps: [
      "Scar tissue and adhesion",
      "Chronic tendinopathy",
      "Fascial restriction",
      "Post-surgical tissue quality",
    ],
  },
  {
    slug: "reflexology-aromatherapy",
    category: "oriental",
    icon: "aroma",
    title: "Reflexology & Aromatherapy",
    short:
      "Pressure-point foot work and therapeutic essential oils for tension, sleep and stress-driven pain.",
    intro:
      "Reflexology applies structured pressure to points on the feet; aromatherapy uses therapeutic essential oils through massage and inhalation. Both are used here for the states that sit alongside persistent pain — muscular tension, disturbed sleep and stress — rather than as treatment for a structural diagnosis.",
    helps: [
      "Stress-related muscular tension",
      "Sleep disturbance",
      "General fatigue",
      "Relaxation alongside active treatment",
    ],
  },

  /* ---------------- Fitness Programs ---------------- */
  {
    slug: "strength-weight-training",
    category: "fitness",
    icon: "strength",
    title: "Strength & Weight Training",
    short:
      "Progressive resistance training programmed clinically, around your injury history.",
    intro:
      "Strength training is the most reliable protection against recurrence of most musculoskeletal injuries. Programmed clinically, it works around the structures that need protecting while progressively loading everything else — so you build capacity without re-provoking the original problem.",
    helps: [
      "Injury recurrence prevention",
      "Rebuilding strength after immobilisation",
      "Osteoarthritis and bone density support",
      "General strength with an injury history",
    ],
  },
  {
    slug: "functional-bodyweight-training",
    category: "fitness",
    icon: "functional",
    title: "Functional & Bodyweight Training",
    short:
      "Training the movements your day actually asks of you — no equipment required.",
    intro:
      "Functional training rebuilds the specific patterns daily life demands: lifting, carrying, stairs, overhead reach, getting off the floor. Because it needs no equipment, it transfers directly into a home programme you can maintain without a gym.",
    helps: [
      "Return to daily activity after injury",
      "Lifting and carrying capacity",
      "Home-based training",
      "General movement quality",
    ],
  },
  {
    slug: "therapeutic-yoga-mobility",
    category: "fitness",
    icon: "yoga",
    title: "Therapeutic Yoga & Mobility",
    short:
      "Mobility, breath and stability sequenced around your restrictions rather than a class format.",
    intro:
      "Yoga-derived movement is useful when it is selected for the individual. Sequences are built around what your assessment showed — working into the ranges you actually lack while protecting the segments that are already too mobile, which a general class cannot do.",
    helps: [
      "Reduced spinal and hip mobility",
      "Breathing pattern and stability work",
      "Stress-related tension",
      "Gentle re-entry to movement",
    ],
  },
  {
    slug: "tai-chi-qigong",
    category: "fitness",
    icon: "taichi",
    title: "Tai Chi, Qigong & Mindful Movement",
    short:
      "Slow weight-shifting practice for balance, joint control and nervous system recovery.",
    intro:
      "Tai Chi's slow, controlled weight transfer is among the better-supported approaches to balance and fall prevention, which makes it valuable in geriatric and post-stroke work. Qigong's breath-led movement is used to down-regulate the nervous system in persistent-pain presentations.",
    helps: [
      "Balance and fall prevention",
      "Joint position sense",
      "Persistent pain and nervous system sensitivity",
      "Post-stroke movement control",
    ],
  },
  {
    slug: "return-to-sport",
    category: "fitness",
    icon: "hiit",
    title: "Injury Recovery & Return-to-Sport",
    short:
      "Criteria-based progression back to full training — you go back when you measure ready, not on a date.",
    intro:
      "Returning to sport too early is the most common reason injuries recur. Progression here is criteria-based: strength symmetry, control under fatigue and sport-specific testing decide when each stage is cleared, rather than how many weeks have passed.",
    helps: [
      "Return after ACL or significant injury",
      "Recurrent injury patterns",
      "Sport-specific conditioning",
      "Confidence returning to competition",
    ],
  },
];

/* --- Lookups ---------------------------------------------------- */

export const servicesByCategory = (key) =>
  services.filter((s) => s.category === key);

export const findService = (slug) => services.find((s) => s.slug === slug);

/** Full URL path for a service or category. */
export const servicePath = (service) => {
  const cat = categories[service.category];
  return cat.childPages
    ? `/${cat.slug}/${service.slug}`
    : `/${cat.slug}#${service.slug}`;
};

export const categoryPath = (key) => `/${categories[key].slug}`;
